import { useEffect, useMemo, useState } from "react";
import { IndianRupee, Users } from "lucide-react";
import { salaryApi, DailyBreakdown, SalaryRecord } from "../../api/salary";
import PayDeductionDetails from "../../components/PayDeductionDetails";
import { useAuth } from "../../contexts/AuthContext";
import { useAccess } from "../../contexts/AccessContext";
import { dayStatusMeta } from "../../utils/dayStatus";
import PayrollRecord from "../PayrollRecord";

// Approval lifecycle → badge. Mirrors PayrollRecord's STATUS_META plus the
// "not_generated" state the self-service endpoint can return.
type MyStatus = "approved" | "sent" | "pending" | "not_generated" | "not_joined";
const STATUS_META: Record<MyStatus, { label: string; cls: string }> = {
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  sent: { label: "Sent for Approval", cls: "bg-blue-100 text-blue-700" },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
  not_generated: { label: "Not generated", cls: "bg-gray-100 text-gray-600" },
  not_joined: { label: "Not joined yet", cls: "bg-slate-100 text-slate-600" },
};

// Calculation-method badge colours (mirror the admin Payroll Records page).
const METHOD_CLS: Record<string, string> = {
  Attendance: "bg-indigo-100 text-indigo-700",
  Workflow: "bg-emerald-100 text-emerald-700",
  "No Deduction": "bg-orange-100 text-orange-700",
};

// Shape returned by GET /salary/my_salary.
interface MySalaryRecord {
  name: string;
  employeeID: string;
  department: string;
  designation: string;
  month: number;
  year: number;
  monthlySalary: number;
  takeHome: number;
  netSalary: number;
  bonus: number;
  extrapay: number;
  totalSalary: number;
  calculationMethod: string;
}
interface MySalarySummary {
  presentDays: number;
  paidLeaveDays: number;
  absentDays: number;
  absentDeduction: number;
  holidayPaidDays: number;
  holidayPaidAmount: number;
  // Why the net is smaller than the days worked suggest. Null when the month
  // carried no late-coming charge / no hours shortfall.
  lateComing: SalaryRecord["lateComing"];
  hoursShortfall: SalaryRecord["hoursShortfall"];
}
interface MySalaryResponse {
  generated: boolean;
  shared: boolean;
  status: MyStatus;
  record: MySalaryRecord | null;
  breakdown: DailyBreakdown[];
  summary?: Partial<MySalarySummary>;
  joiningDate?: string | null;
  message?: string;
}

const months = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

type TabKey = "mine" | "team";

const MyPayroll = () => {
  const { user } = useAuth();
  const { hasAccess, canManage } = useAccess();

  // Non-employee/intern roles that can also manage payroll get the Team tab
  // (which reuses the role-scoped PayrollRecord page).
  const canGenerate =
    user?.role !== "employee" &&
    user?.role !== "intern" &&
    (!hasAccess || canManage("payroll-records"));

  const [tab, setTab] = useState<TabKey>("mine");

  // "Last month" defaults: e.g. today 2026-07 -> 2026-06.
  const lastMonthDate = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d;
  }, []);
  const [selectedYear, setSelectedYear] = useState(String(lastMonthDate.getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(
    String(lastMonthDate.getMonth() + 1).padStart(2, "0")
  );

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => ({
    value: y.toString(),
    label: y.toString(),
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<MySalaryResponse | null>(null);

  const monthLabel = (m: string) => months.find((mm) => mm.value === m)?.label || m;

  // Fetch the current user's own salary for the chosen period.
  useEffect(() => {
    let cancelled = false;
    const fetchMine = async () => {
      setIsLoading(true);
      try {
        const res = await salaryApi.getMySalary(selectedYear, selectedMonth);
        if (!cancelled) setData(res.data as MySalaryResponse);
      } catch (error) {
        console.error("Failed to fetch my salary:", error);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchMine();
    return () => {
      cancelled = true;
    };
  }, [selectedYear, selectedMonth]);

  const status: MyStatus = data?.status ?? "not_generated";
  const statusMeta = STATUS_META[status];
  const record = data?.record ?? null;
  // Backend `netSalary` already bakes in bonus + extra pay, so the pure
  // attendance-calculated figure is netSalary minus those additions — and the
  // real total paid is netSalary itself (re-adding bonus/extra double-counts,
  // which is what the old `totalSalary` did).
  const bonusAmt = record?.bonus || 0;
  const extraAmt = record?.extrapay || 0;
  const calculatedNet = (record?.netSalary || 0) - bonusAmt - extraAmt;
  const totalPaid = record?.netSalary || 0;
  const breakdown = useMemo(() => data?.breakdown ?? [], [data?.breakdown]);
  const shared = !!data?.shared;
  const payrollSummary: MySalarySummary = useMemo(() => {
    const fromApi = data?.summary || {};
    const paidHolidayStatuses = new Set(["HOLIDAY_PAID", "SUNDAY_PAID", "SATURDAY_PAID"]);
    const presentStatuses = new Set(["PRESENT", "FULL_DAY", "WORK_FROM_HOME"]);
    const paidHolidayRows = breakdown.filter((day) => paidHolidayStatuses.has(day.status));
    const presentDays = breakdown.filter((day) => presentStatuses.has(day.status)).length;
    const paidLeaveDays = breakdown.filter((day) => day.status === "PAID_LEAVE").length;
    const absentDays = breakdown.filter((day) => day.status === "ABSENT").length;

    return {
      presentDays: fromApi.presentDays ?? presentDays,
      paidLeaveDays: fromApi.paidLeaveDays ?? paidLeaveDays,
      absentDays: fromApi.absentDays ?? absentDays,
      absentDeduction:
        fromApi.absentDeduction ??
        absentDays * (breakdown.find((day) => day.day_salary)?.day_salary || 0),
      holidayPaidDays: fromApi.holidayPaidDays ?? paidHolidayRows.length,
      holidayPaidAmount:
        fromApi.holidayPaidAmount ??
        paidHolidayRows.reduce((sum, day) => sum + (day.day_salary || 0), 0),
      // No client-side fallback on purpose: both are settled once per month by
      // payroll and cannot be re-derived from the daily rows. Absent means the
      // server did not send them, and inventing a figure here would be worse
      // than showing none.
      lateComing: fromApi.lateComing ?? null,
      hoursShortfall: fromApi.hoursShortfall ?? null,
    };
  }, [breakdown, data?.summary]);
  const emptyTitle = status === "not_joined" ? "Not joined for this period" : "Nothing to show yet";
  const emptyMessage =
    status === "not_joined"
      ? data?.message ||
        `You had not joined the organisation for ${monthLabel(selectedMonth)} ${selectedYear}.`
      : status === "not_generated"
      ? `Your salary for ${monthLabel(selectedMonth)} ${selectedYear} hasn't been generated yet.`
      : `Your salary for ${monthLabel(selectedMonth)} ${selectedYear} hasn't been shared with you yet.`;

  const money = (n?: number | null) => `₹${(n ?? 0).toLocaleString()}`;

  const renderModeSwitch = () =>
    canGenerate ? (
      <div
        className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1"
        aria-label="Payroll view"
      >
        <button
          type="button"
          onClick={() => setTab("mine")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            tab === "mine"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          My Salary
        </button>
        <button
          type="button"
          onClick={() => setTab("team")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors flex items-center gap-1.5 ${
            tab === "team"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          Team
        </button>
      </div>
    ) : null;

  const renderMyView = () => (
    <div className="p-6">
      {/* Period filters */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-600 mb-1">Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {years.map((year) => (
              <option key={year.value} value={year.value}>
                {year.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-600 mb-1">Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status badge */}
        <div className="flex flex-col ml-auto">
          <span className="text-xs font-semibold text-gray-600 mb-1">Status</span>
          <span
            className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full ${statusMeta.cls}`}
          >
            {statusMeta.label}
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl mx-auto mb-4 flex items-center justify-center animate-pulse">
            <IndianRupee className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">Loading your salary...</p>
        </div>
      ) : !shared ? (
        // Empty state — either not generated yet, or generated but not shared.
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <IndianRupee className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-500 mb-1">{emptyTitle}</h3>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            {emptyMessage}
          </p>
        </div>
      ) : (
        <>
          {/* Headline: the amount actually paid this month + how it was computed */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
            <div>
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                Total Salary Paid
              </p>
              <p className="text-2xl font-bold text-emerald-700">{money(totalPaid)}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Net {money(calculatedNet)} + Bonus {money(bonusAmt)} + Extra {money(extraAmt)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                Calculation Method
              </p>
              <span
                className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                  METHOD_CLS[record?.calculationMethod || ""] || "bg-gray-100 text-gray-700"
                }`}
              >
                {record?.calculationMethod || "—"}
              </span>
            </div>
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">Employee ID</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={record?.employeeID}>
                {record?.employeeID || "—"}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">Department</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={record?.department}>
                {record?.department || "—"}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">Monthly Salary</p>
              <p className="text-sm font-semibold text-gray-900">{money(record?.monthlySalary)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">TakeHome Salary</p>
              <p className="text-sm font-semibold text-gray-900">{money(record?.takeHome)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">Net Salary</p>
              <p className="text-sm font-bold text-emerald-600">{money(calculatedNet)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">Bonus</p>
              <p className="text-sm font-semibold text-amber-600">{money(record?.bonus)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 mb-1">Extra Pay</p>
              <p className="text-sm font-semibold text-blue-600">{money(record?.extrapay)}</p>
            </div>
          </div>

          {/* Payroll summary */}
          <h3 className="text-sm font-bold text-gray-800 mb-3">Payroll Summary</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div
              data-testid="payroll-summary-present"
              className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3"
            >
              <p className="text-xs text-emerald-700 mb-1">Present</p>
              <p className="text-sm font-bold text-emerald-800">
                {payrollSummary.presentDays.toLocaleString()} days
              </p>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-3">
              <p className="text-xs text-teal-700 mb-1">Paid Leave</p>
              <p className="text-sm font-bold text-teal-800">
                {payrollSummary.paidLeaveDays.toLocaleString()} days
              </p>
            </div>
            <div
              data-testid="payroll-summary-absent"
              className="bg-red-50 border border-red-200 rounded-lg px-4 py-3"
            >
              <p className="text-xs text-red-700 mb-1">Absent</p>
              <p className="text-sm font-bold text-red-800">
                {payrollSummary.absentDays.toLocaleString()} days
              </p>
              <p className="mt-1 text-[11px] font-semibold text-red-700">
                {money(payrollSummary.absentDeduction)}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <p className="text-xs text-blue-700 mb-1">Paid Holidays</p>
              <p className="text-sm font-bold text-blue-800">
                {payrollSummary.holidayPaidDays.toLocaleString()} days
              </p>
              <p className="text-[11px] text-blue-600 mt-0.5">
                {money(payrollSummary.holidayPaidAmount)}
              </p>
            </div>
          </div>

          {/* The two charges the daily table cannot show, because neither
              belongs to any single day. This is the employee's own payslip —
              if the explanation is anywhere, it has to be here. */}
          {(payrollSummary.lateComing?.amount || payrollSummary.hoursShortfall?.amount) ? (
            <div
              data-testid="payroll-summary-deductions"
              className="mb-6 rounded-lg border border-gray-200"
            >
              <PayDeductionDetails
                record={{
                  lateComing: payrollSummary.lateComing,
                  hoursShortfall: payrollSummary.hoursShortfall,
                }}
                dailyData={breakdown}
              />
            </div>
          ) : null}

          {/* Daily attendance breakdown */}
          <h3 className="text-sm font-bold text-gray-800 mb-3">Daily Attendance Breakdown</h3>
          {breakdown.length === 0 ? (
            <div className="text-center py-8 border border-gray-200 rounded-lg">
              <p className="text-gray-500 text-sm">No attendance data available</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[60vh] border border-gray-200 rounded-lg">
              <table className="w-full text-xs min-w-[600px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-700">Hours</th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-semibold text-gray-700">Payable</th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-semibold text-gray-700">Overtime</th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-semibold text-gray-700">Day Salary</th>
                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-semibold text-gray-700">OT Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {breakdown.map((day, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                        {new Date(day.date).toLocaleDateString("en-IN")}
                      </td>
                      <td className="px-3 py-2 text-gray-700 font-mono">{day.hours}h</td>
                      <td className="px-3 py-2">
                        {(() => {
                          // Friendly label for the raw status code
                          // (SUNDAY_PAID → Sunday, PAID_LEAVE → Leave, …).
                          const meta = dayStatusMeta(day.status);
                          return (
                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.cls}`}>
                              {meta.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-gray-600">{day.payable}h</td>
                      <td className="px-3 py-2 text-right font-mono text-purple-600">
                        {day.overtime}h
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-blue-600">
                        ₹{day.day_salary?.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600">
                        ₹{day.overtime_amount?.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderSelfServiceShell = () => (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white shadow rounded-2xl">
          <div className="px-6 pt-6 pb-4 border-b border-gray-200">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-800 mb-1">Payroll Records</h1>
                <p className="text-sm text-gray-500 max-w-3xl">
                  Your salary breakdown and approval status.
                </p>
              </div>
              <div className="shrink-0 sm:pt-1">{renderModeSwitch()}</div>
            </div>
          </div>

          {renderMyView()}
        </div>
      </div>
    </div>
  );

  if (!canGenerate || tab === "mine") {
    return renderSelfServiceShell();
  }

  return (
    <PayrollRecord
      headerActions={renderModeSwitch()}
      includeCurrentUserInEmployeeList={canGenerate}
    />
  );
};

export default MyPayroll;
